import { toaster } from '@optiaxiom/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { extractApiErrorMessage } from '@frontend/api/http';
import { UserAuthConnection } from '@frontend/types';

import { getUserAuthConnections, initiateAuthConnection, revokeAuthConnection } from '../services';

export function useGetUserAuthConnections(userId: string) {
  const { data, isPending, error } = useQuery<UserAuthConnection[]>({
    queryKey: getUserAuthConnections(userId).key,
    queryFn: getUserAuthConnections(userId).queryFn,
    enabled: !!userId,
  });

  return { connections: data ?? [], isPending, error };
}

type InitiateAuthParams = {
  provider: string;
  scopeBundleId: string;
  userId: string;
  redirectUri: string;
};

export function useInitiateAuthConnection(onSuccess?: (authUrl: string) => void) {
  const { mutate, isPending, error } = useMutation({
    mutationFn: (params: InitiateAuthParams) => initiateAuthConnection(params),
    onSuccess: response => {
      const authUrl = response.data.auth_url;
      if (authUrl) {
        onSuccess?.(authUrl);
      }
    },
    onError: error => {
      const errorMessage = extractApiErrorMessage(error);
      toaster.create(errorMessage, { type: 'danger' });
    },
  });

  return { initiateAuth: mutate, isInitiating: isPending, error };
}

export function useRevokeAuthConnection(userId: string) {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, string>({
    mutationFn: credentialId => revokeAuthConnection(userId, credentialId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getUserAuthConnections(userId).key });
      toaster.create('Connection revoked successfully', { type: 'success' });
    },
    onError: () => {
      toaster.create('Failed to revoke connection', { type: 'danger' });
    },
  });
}
